import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';
import { toast } from 'react-toastify';

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    password: '',
    password_confirmation: ''
  });
  const [countryCode, setCountryCode] = useState('+974'); // Qatar by default
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();

  const countryCodes = [
    { code: '+974', country: 'QA', name: 'Qatar', flag: '🇶🇦' },
    { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+965', country: 'KW', name: 'Kuwait', flag: '🇰🇼' },
    { code: '+973', country: 'BH', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+968', country: 'OM', name: 'Oman', flag: '🇴🇲' },
    { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.password_confirmation) {
      toast.error('Passwords do not match!');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    // Combine country code with mobile number if mobile is provided
    const submissionData = {
      ...formData,
      mobile: formData.mobile ? `${countryCode}${formData.mobile}` : ''
    };
    const result = await register(submissionData, false);
    
    if (result.success) {
      navigate('/login');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[560px]">
        <div className="auth-card px-7 py-9 sm:px-10">
          <Link to="/" className="auth-back">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t('backHome')}
          </Link>
          <h1 className="text-[1.75rem] font-medium text-gray-900 tracking-tight">{t('createAccount')}</h1>
          <p className="mt-2 text-sm text-gray-500 leading-relaxed">
            {t('createLead')}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">{t('firstName')}</label>
                <input id="first_name" type="text" name="first_name" required autoComplete="given-name" placeholder="First name" value={formData.first_name} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">{t('lastName')}</label>
                <input id="last_name" type="text" name="last_name" required autoComplete="family-name" placeholder="Last name" value={formData.last_name} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">{t('email')}</label>
              <input id="email" type="email" name="email" required autoComplete="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} className="auth-input auth-input-plain" />
            </div>

            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">{t('mobile')} <span className="text-gray-400 font-normal">{t('optional')}</span></label>
              <div className="flex gap-3">
                <select
                  aria-label="Country code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="auth-input auth-input-plain w-[128px] shrink-0"
                >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.code}
                        </option>
                      ))}
                </select>
                <input id="mobile" type="tel" name="mobile" autoComplete="tel-national" placeholder={t('mobileNumber')} value={formData.mobile} onChange={handleChange} pattern="[0-9]*" maxLength="15" className="auth-input auth-input-plain" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">{t('password')}</label>
                <input id="password" type="password" name="password" required autoComplete="new-password" placeholder="At least 6 characters" value={formData.password} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
              <div>
                <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">{t('confirmPassword')}</label>
                <input id="password_confirmation" type="password" name="password_confirmation" required autoComplete="new-password" placeholder="Repeat password" value={formData.password_confirmation} onChange={handleChange} className="auth-input auth-input-plain" />
              </div>
            </div>

            <label htmlFor="terms" className="flex items-start gap-3 text-sm text-gray-600">
              <input id="terms" type="checkbox" required className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-700" />
              <span>
                {t('agreeBefore')}{' '}
                <Link to="/terms" className="text-purple-700 hover:underline">{t('terms')}</Link>
                {' '}{t('agreeMid')}{' '}
                <Link to="/privacy" className="text-purple-700 hover:underline">{t('privacy')}</Link>
                {' '}{t('agreeAfter')}
              </span>
            </label>

            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? t('creating') : t('createAccount')}
            </button>
          </form>

          <p className="mt-7 pt-6 text-center text-sm text-gray-500 border-t border-[#f3f2f6]">
            {t('already')}{' '}
            <Link to="/login" className="nav-link font-medium text-purple-700 hover:text-purple-900">{t('signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;

